import { useState, useEffect } from "react";
import { Shield, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { refreshAdminStatus } from "@/lib/admin-status";
import { useAuth } from "@/lib/auth";

async function checkAdminCookie(): Promise<boolean> {
  return refreshAdminStatus();
}

export function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading, login } = useAuth();
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needs2FA, setNeeds2FA] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    async function init() {
      if (user?.role === "admin") {
        setAuthenticated(true);
        return;
      }

      const isAuth = await checkAdminCookie();
      setAuthenticated(isAuth);
    }
    if (!authLoading) {
      init();
    }
  }, [user, authLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    setError(false);
    setErrorMessage("");

    const result = await login(email, password, needs2FA ? totpCode : undefined);
    if (result.requires2FA) {
      setNeeds2FA(true);
      setIsVerifying(false);
      return;
    }
    if (result.error) {
      setError(true);
      setErrorMessage(result.error);
      if (needs2FA) {
        setTotpCode("");
      } else {
        setPassword("");
      }
    } else {
      const checkRes = await checkAdminCookie();
      if (checkRes) {
        setAuthenticated(true);
      } else {
        setError(true);
        setErrorMessage("This account does not have admin access.");
      }
    }
    setIsVerifying(false);
  };

  if (authenticated === null || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-sm space-y-6 animate-in fade-in duration-300">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center mx-auto shadow-[0_0_20px_rgba(59,130,246,0.2)]">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Admin Access Required</h1>
              <p className="text-sm text-muted-foreground mt-1 font-mono">RESONANCE Control Panel</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 bg-black/40 border border-border/50 rounded-xl p-6">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Email</Label>
              <Input
                data-testid="input-admin-email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={`bg-black/50 font-mono ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Password</Label>
              <Input
                data-testid="input-admin-password"
                type="password"
                placeholder="Your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={`bg-black/50 font-mono ${error && !needs2FA ? "border-destructive focus-visible:ring-destructive" : ""}`}
                disabled={needs2FA}
              />
            </div>
            {needs2FA && (
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase text-muted-foreground">2FA Code</Label>
                <Input
                  data-testid="input-admin-totp"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value)}
                  className={`bg-black/50 font-mono ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
                  autoFocus
                  maxLength={8}
                />
              </div>
            )}
            {error && (
              <p className="text-xs text-destructive font-mono" data-testid="text-auth-error">
                {errorMessage || "Invalid credentials. Access denied."}
              </p>
            )}

            <Button
              data-testid="button-admin-login"
              type="submit"
              disabled={isVerifying || !email || !password}
              className="w-full hover-elevate shadow-lg shadow-primary/20"
            >
              {isVerifying ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verifying...</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" />Access Control Panel</>
              )}
            </Button>
          </form>

          <p className="text-center text-xs font-mono text-muted-foreground/40">
            OPERATOR INTERFACE - AUTHORIZED PERSONNEL ONLY
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
